import Phaser from 'phaser';
import { FONT_FAMILY, GAME_WIDTH, getGameHeight } from '../config/constants';
import { crispText } from './textUtils';

const PANEL_WIDTH = 640;
const PANEL_X = (GAME_WIDTH - PANEL_WIDTH) / 2;
const OVERLAY_DEPTH = 200;
const ROW_HEIGHT = 44;
const LABEL_X = PANEL_X + 40;
const VALUE_X = PANEL_X + PANEL_WIDTH - 40;

export interface LevelInfoData {
  level: number;
  name?: string;
  tier: number;
  gridRows: number;
  gridCols: number;
  cellCount: number;
  colorCount: number;
  colorNames: string[];
  winningSeed?: number;
  effectiveP?: number;
  hasSnakeCols: boolean;
  hasMegaBlocks: boolean;
  hasLockedDoors: boolean;
  hasBlockadeWalls: boolean;
  hasBarricades: boolean;
  hasHammers: boolean;
  hasFrozenIndices: boolean;
  attackerCount: number;
  difficulty: {
    multiSlotChance: number;
    multiColorChance: number;
    mysteryChance: number;
    frozenChance: number;
    minAmmo: number;
    maxAmmo: number;
    ammoSurplusRatio: number;
  };
}

export class InfoOverlay {
  private container: Phaser.GameObjects.Container;
  private scene: Phaser.Scene;
  private escKey?: Phaser.Input.Keyboard.Key;

  constructor(scene: Phaser.Scene, data: LevelInfoData) {
    this.scene = scene;
    this.container = scene.add.container(0, 0).setDepth(OVERLAY_DEPTH).setVisible(false).setAlpha(0);

    // Semi-transparent dark overlay (full screen, blocks clicks)
    const dimBg = scene.add.rectangle(GAME_WIDTH / 2, getGameHeight() / 2, GAME_WIDTH, getGameHeight(), 0x000000, 0.7);
    dimBg.setInteractive(); // Block clicks through to game
    dimBg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      this.hide();
    });
    this.container.add(dimBg);

    // Build rows of data
    const rows: [string, string][] = [];
    rows.push(['Level', `${data.level}`]);
    if (data.name) rows.push(['Name', data.name]);
    rows.push(['Tier', `${data.tier} / 12`]);
    rows.push(['Grid', `${data.gridRows} x ${data.gridCols}`]);
    rows.push(['Cells', `${data.cellCount}`]);
    rows.push(['Colors', `${data.colorCount} (${data.colorNames.join(', ')})`]);
    rows.push(['Attackers', `${data.attackerCount}`]);

    // Queue easing has been removed — queues are always fully random.
    rows.push(['Queue Easing', 'None — fully random']);

    // Special features
    const specials: string[] = [];
    if (data.hasSnakeCols) specials.push('Snake');
    if (data.hasMegaBlocks) specials.push('Mega');
    if (data.hasLockedDoors) specials.push('Doors');
    if (data.hasBlockadeWalls) specials.push('Blockade');
    if (data.hasBarricades) specials.push('Barricade');
    if (data.hasHammers) specials.push('Hammer');
    if (data.hasFrozenIndices) specials.push('Frozen');
    if (specials.length > 0) {
      rows.push(['Specials', specials.join(', ')]);
    }

    // Bot Results section
    if (data.winningSeed !== undefined || data.effectiveP !== undefined) {
      rows.push(['__HR__', '']); // horizontal rule
      rows.push(['-- Bot Results --', '']);
      if (data.winningSeed !== undefined) rows.push(['Winning Seed', `${data.winningSeed}`]);
      if (data.effectiveP !== undefined) rows.push(['Win Rate', `${Math.round(data.effectiveP * 100)}%`]);
    }

    // Difficulty params
    rows.push(['__HR__', '']); // horizontal rule
    rows.push(['-- Difficulty --', '']);
    rows.push(['Multi-slot', `${Math.round(data.difficulty.multiSlotChance * 100)}%`]);
    rows.push(['Multi-color', `${Math.round(data.difficulty.multiColorChance * 100)}%`]);
    rows.push(['Mystery', `${Math.round(data.difficulty.mysteryChance * 100)}%`]);
    rows.push(['Frozen', `${Math.round(data.difficulty.frozenChance * 100)}%`]);
    rows.push(['Ammo Range', `${data.difficulty.minAmmo}-${data.difficulty.maxAmmo}`]);
    rows.push(['Surplus Ratio', `${data.difficulty.ammoSurplusRatio.toFixed(2)}`]);

    // Build ID
    rows.push(['__HR__', '']); // horizontal rule
    rows.push(['__BUILD_ID__', '']); // special build ID row

    // Calculate panel height
    const titleHeight = 100;
    const contentHeight = rows.length * ROW_HEIGHT + 40;
    const closeButtonHeight = 100;
    const panelHeight = titleHeight + contentHeight + closeButtonHeight;
    const panelY = (getGameHeight() - panelHeight) / 2;

    // Panel background
    const panelGfx = scene.add.graphics();
    panelGfx.fillStyle(0x1a1a2e, 0.95);
    panelGfx.fillRoundedRect(PANEL_X, panelY, PANEL_WIDTH, panelHeight, 32);
    panelGfx.lineStyle(4, 0x4a4a7a, 0.6);
    panelGfx.strokeRoundedRect(PANEL_X, panelY, PANEL_WIDTH, panelHeight, 32);
    // Block clicks on panel from reaching dimBg
    const panelHit = scene.add.rectangle(GAME_WIDTH / 2, panelY + panelHeight / 2, PANEL_WIDTH, panelHeight, 0x000000, 0);
    panelHit.setInteractive();
    panelHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
    });
    this.container.add(panelGfx);
    this.container.add(panelHit);

    // Title
    const titleText = crispText(scene.add.text(GAME_WIDTH / 2, panelY + 56, 'LEVEL INFO', {
      fontFamily: FONT_FAMILY,
      fontSize: '44px',
      color: '#ccccee',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    this.container.add(titleText);

    // Separator line
    const sep = scene.add.graphics();
    sep.lineStyle(2, 0x4a4a7a, 0.4);
    sep.lineBetween(PANEL_X + 32, panelY + 92, PANEL_X + PANEL_WIDTH - 32, panelY + 92);
    this.container.add(sep);

    // Content rows
    let rowY = panelY + titleHeight + 16;
    for (const [label, value] of rows) {
      // Horizontal rule separator
      if (label === '__HR__') {
        rowY += ROW_HEIGHT * 0.5;
        const hrGfx = scene.add.graphics();
        hrGfx.lineStyle(2, 0x4a4a7a, 0.35);
        hrGfx.lineBetween(PANEL_X + 40, rowY, PANEL_X + PANEL_WIDTH - 40, rowY);
        this.container.add(hrGfx);
        rowY += ROW_HEIGHT * 0.5;
        continue;
      }

      if (label === '' && value === '') {
        rowY += ROW_HEIGHT * 0.3;
        continue;
      }

      // Build ID special row
      if (label === '__BUILD_ID__') {
        const buildId = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev';
        const prefix = buildId.slice(0, -3);
        const suffix = buildId.slice(-3);
        rowY += 8; // extra top padding

        const buildPrefix = crispText(scene.add.text(0, rowY, prefix, {
          fontFamily: FONT_FAMILY,
          fontSize: '22px',
          color: '#555577',
        })).setOrigin(0, 0.5);

        const buildSuffix = crispText(scene.add.text(0, rowY, suffix, {
          fontFamily: FONT_FAMILY,
          fontSize: '26px',
          color: '#ff6666',
          fontStyle: 'bold',
        })).setOrigin(0, 0.5);

        // Center the combined build ID text
        const totalWidth = buildPrefix.width + buildSuffix.width;
        const startX = GAME_WIDTH / 2 - totalWidth / 2;
        buildPrefix.setX(startX);
        buildSuffix.setX(startX + buildPrefix.width);

        this.container.add(buildPrefix);
        this.container.add(buildSuffix);
        rowY += ROW_HEIGHT;
        continue;
      }

      const isSectionHeader = label.startsWith('--');
      const displayLabel = isSectionHeader ? label.replace(/--/g, '').trim() : label;

      // Hover highlight rectangle (behind the row content)
      if (!isSectionHeader) {
        const hoverBg = scene.add.rectangle(
          GAME_WIDTH / 2, rowY,
          PANEL_WIDTH - 32, ROW_HEIGHT,
          0x4a4a7a, 0
        ).setInteractive({ useHandCursor: false });
        hoverBg.on('pointerover', () => hoverBg.setAlpha(1).setFillStyle(0x4a4a7a, 0.15));
        hoverBg.on('pointerout', () => hoverBg.setAlpha(1).setFillStyle(0x4a4a7a, 0));
        this.container.add(hoverBg);
      }

      const labelText = crispText(scene.add.text(LABEL_X, rowY, displayLabel, {
        fontFamily: FONT_FAMILY,
        fontSize: isSectionHeader ? '26px' : '24px',
        color: isSectionHeader ? '#8888bb' : '#9999bb',
        fontStyle: isSectionHeader ? 'bold' : 'normal',
      })).setOrigin(0, 0.5);
      this.container.add(labelText);

      if (value && !isSectionHeader) {
        const valueText = crispText(scene.add.text(VALUE_X, rowY, value, {
          fontFamily: FONT_FAMILY,
          fontSize: '24px',
          color: '#ccccee',
        })).setOrigin(1, 0.5);
        this.container.add(valueText);
      }

      rowY += ROW_HEIGHT;
    }

    // Close button (X) in top-right of panel
    const closeX = PANEL_X + PANEL_WIDTH - 48;
    const closeY = panelY + 48;
    const closeBtn = crispText(scene.add.text(closeX, closeY, '\u2715', {
      fontFamily: FONT_FAMILY,
      fontSize: '36px',
      color: '#888899',
    }), false).setOrigin(0.5).setDepth(OVERLAY_DEPTH + 1);
    this.container.add(closeBtn);

    const closeHit = scene.add.rectangle(closeX, closeY, 64, 64, 0x000000, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(OVERLAY_DEPTH + 1);
    this.container.add(closeHit);

    closeHit.on('pointerover', () => closeBtn.setColor('#ffffff'));
    closeHit.on('pointerout', () => closeBtn.setColor('#888899'));
    closeHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      this.hide();
    });
  }

  show(): void {
    this.container.setVisible(true);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: 150,
      ease: 'Sine.easeOut',
    });

    // ESC key to close (only while visible)
    if (this.scene.input.keyboard) {
      this.escKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
      this.escKey.once('down', () => this.hide());
    }
  }

  hide(): void {
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 150,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.container.setVisible(false);
        this.destroy();
      },
    });
  }

  destroy(): void {
    if (this.escKey) {
      this.escKey.removeAllListeners();
      this.scene.input.keyboard?.removeKey(this.escKey);
      this.escKey = undefined;
    }
    this.container.destroy();
  }

  get visible(): boolean {
    return this.container.visible;
  }
}
